"use client";

import { useState, type CSSProperties } from "react";

import {
  articleYoutubeFrameClassName,
  articleYoutubePlayButtonClassName,
  articleYoutubePlayerClassName,
  articleYoutubePlayerCropClassName,
  articleYoutubePlayIconClassName,
} from "./article-typography";
import type { NormalizedSocialEmbed } from "./social-embed";

type Props = {
  embed: NormalizedSocialEmbed;
  title?: string;
};

const iframeAllow =
  "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";

const youtubeThumbStyle = (thumbnail: string) =>
  ({
    backgroundImage: `linear-gradient(0deg, #00000033 0%, #00000033 100%), url(${thumbnail})`,
  }) satisfies CSSProperties;

const YoutubeLoomPlayer = ({ embed, title }: Props) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoId = embed.normalizedUrl.split("/").pop() ?? "";
  const thumbnail =
    embed.provider === "youtube"
      ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`
      : `https://cdn.loom.com/sessions/thumbnails/${videoId}-00001.jpg`;

  return (
    <div className={articleYoutubeFrameClassName} style={youtubeThumbStyle(thumbnail)}>
      {isPlaying ? (
        <div className={articleYoutubePlayerCropClassName}>
          <iframe
            src={`${embed.normalizedUrl}?autoplay=1`}
            className={articleYoutubePlayerClassName}
            allow={iframeAllow}
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            title={title}
          />
        </div>
      ) : (
        <button
          type="button"
          className={articleYoutubePlayButtonClassName}
          onClick={() => setIsPlaying(true)}
          aria-label="Play video"
        >
          <svg
            width="72"
            height="72"
            viewBox="0 0 72 72"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
            className={articleYoutubePlayIconClassName}
          >
            <circle cx="36" cy="36" r="36" fill="#472695" />
            <path
              d="M50.2891 35.9607L26.2891 49.8171L26.2891 22.1043L50.2891 35.9607Z"
              fill="white"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

/**
 * Renders a social embed inside an article. TikTok and Instagram keep the
 * native portrait frame; YouTube and Loom use the article video player.
 */
export const SocialEmbedPlayer = ({
  embed,
  title = "Embedded media",
}: Props) => {
  if (embed.provider === "youtube" || embed.provider === "loom") {
    return <YoutubeLoomPlayer embed={embed} title={title} />;
  }

  return (
    <div className="relative h-[745.342px] w-[320px] shrink-0 self-start overflow-hidden bg-[#d3d3d3] mr-[38px] min-[641px]:mr-[244px] md:mr-[352px] lg:mr-[400px] xl:mr-0">
      <iframe
        src={embed.normalizedUrl}
        className="absolute inset-0 h-full w-full border-0"
        allow={iframeAllow}
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
        title={title}
      />
    </div>
  );
};
